import React, { createContext, FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { getUserDataWithUsername, IUserProps } from '../common/data/userDummyData';
import { useQueryClient } from '@tanstack/react-query';

export interface IAuthContextProps {
	user: string;
	setUser?(...args: unknown[]): unknown;
	setToken?(...args: unknown[]): unknown;
	setRights?(...args: unknown[]): unknown;
	userData: Partial<IUserProps>;
	logout: () => Promise<void>;
}
export interface IAuthContext {
	user: string;
	setUser: React.Dispatch<React.SetStateAction<string>>;
	token: string;
	setToken: React.Dispatch<React.SetStateAction<string>>;
	rights: string;
	setRights: React.Dispatch<React.SetStateAction<string>>;
	userData: Partial<IUserProps>;
	logout: () => Promise<void>;
}
export const STORAGE_KEYS = {
	token: 'accessToken',
	refreshToken: 'refreshToken',
	user: 'neels_authUsername',
	rights: 'neels_rights',
	username: 'username',
	receiverId: 'receverId',
	loggedInUserId: 'loggedin_user_id',
};

const AuthContext = createContext<IAuthContextProps>({} as IAuthContextProps);

interface IAuthContextProviderProps {
	children: ReactNode;
}
export const AuthContextProvider: FC<IAuthContextProviderProps> = ({ children }) => {
	const queryClient = useQueryClient();
	const [authVersion, setAuthVersion] = useState(0);
	const [user, setUser] = useState<string>(localStorage?.getItem('neels_authUsername') || '');
	const [token, setToken] = useState<string>(localStorage?.getItem('authToken') || '');
	const [rights, setRights] = useState<string>(localStorage.getItem('neels_rights') || '');
	const [userData, setUserData] = useState<Partial<IUserProps>>({});

	const logout = async () => {
		// 1 Clear React state FIRST
		setUser('');
		setToken('');
		setRights('');

		setAuthVersion((v) => v + 1);

		// 2 Remove auth keys
		Object.values(STORAGE_KEYS).forEach((key) => {
			localStorage.removeItem(key);
			sessionStorage.removeItem(key);
		});

		queryClient.clear();

		// 3 Clear cache (PWA)
		if ('caches' in window) {
			const keys = await caches.keys();
			await Promise.all(keys.map((key) => caches.delete(key)));
		}

		// 4 Unregister service workers
		if ('serviceWorker' in navigator) {
			const registrations = await navigator.serviceWorker.getRegistrations();
			await Promise.all(registrations.map((reg) => reg.unregister()));
		}
	};

	useEffect(() => {
		localStorage.setItem('neels_authUsername', user);
		localStorage.setItem('authToken', token);
		localStorage.setItem('accessToken', token);
		localStorage.setItem('neels_rights', rights);
	}, [user, token]);

	useEffect(() => {
		if (user !== '') {
			setUserData(getUserDataWithUsername(user));
		} else {
			setUserData({});
		}
	}, [user]);

	const value = useMemo(
		() => ({
			user,
			setUser,
			token,
			setToken,
			rights,
			setRights,
			userData,
			logout, // exposed
		}),
		[user, userData, token],
	);
	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
